/**
 * Which scaffolder template plays which ROLE for a project — its creation,
 * its deletion, the wake, the sleep, the promotion and the change of
 * configuration of one of its environments — step 7 of the platform plan
 * (2026-10-20). The frontend half of
 * `plugins/waadoo-core-backend/src/plugins/shared/templateRoles.ts`.
 *
 * DISPLAY AND ROUTING ONLY: which template a button launches, which page and
 * title a task gets. The backend actions decide what may run (their template
 * locks); a template launched here that the backend does not accept is
 * refused by its task.
 *
 * A project's templates come from its `AppType` (`spec.templates`: `create`,
 * `delete`, `env.{configure,promote,restore,sleep,wake}`), read through the
 * `waadoo-platform` route (`appTypes.ts`), and from it only. Pour un projet
 * Odoo (`LEGACY_APP_TYPE`), tant que son descripteur se lit, et pour de bon
 * quand il manque ou échoue, ceux du descripteur Odoo embarqué
 * (`EMBEDDED_ODOO_TEMPLATES`) — ce que les verrous du backend acceptent quand
 * le catalogue n'a pas d'`AppType odoo` ou ne se lit pas : rien n'attend le
 * descripteur. For any other type there is no fallback: the role has no
 * template (undefined) until the descriptor says one, and the callers offer
 * no launch meanwhile — an Odoo template run against another type's project
 * would be wrong, not merely refused.
 *
 * A task is recognised by the role of its template (`roleOfTemplate`): the
 * embedded Odoo descriptor's refs, the generic names of step 7, and whatever
 * the descriptors given designate. Les anciens modèles Odoo (`odoo-env-*`,
 * `odoo-project-delete`) ne sont plus reconnus depuis le nettoyage du
 * 2026-10-23 (aucun projet n'en reste) : leurs tâches passées s'affichent
 * comme celles de tout modèle inconnu, sur la page générique du scaffolder.
 */

import { useEffect, useMemo, useState } from 'react';
import { parseEntityRef } from '@backstage/catalog-model';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/frontend-plugin-api';
import {
  EMBEDDED_ODOO_APP_TYPE,
  type AppProfile,
} from '@internal/plugin-waadoo-config-common';
import { fetchAppType, fetchAppTypes, type AppTypeDetail } from './appTypes';
import { LEGACY_APP_TYPE } from './projectIdentity';

export const TEMPLATE_ROLES = [
  'create',
  'delete',
  'wake',
  'sleep',
  'promote',
  'configure',
  'restore',
] as const;

export type TemplateRole = (typeof TEMPLATE_ROLES)[number];

/**
 * A project's template ref per role; a role without one is absent
 * (`projectTemplates`).
 */
export type ProjectTemplates = Readonly<Partial<Record<TemplateRole, string>>>;

/**
 * The creation template of an Odoo project, which the Odoo AppType (and the
 * embedded descriptor) designates: the one the Odoo wizard launches
 * (`plugins/odoo`, `OdooProjectWizard`).
 */
export const ODOO_CREATE_TEMPLATE = 'template:default/odoo-project';

/**
 * The generic templates of step 7 (platform-catalog `scaffolder/env-*`,
 * `scaffolder/project-delete`; backend `GENERIC_TEMPLATES`): recognised on
 * their name for display, whichever AppType designates them.
 */
export const GENERIC_ROLES = [
  'delete',
  'wake',
  'sleep',
  'promote',
  'configure',
  'restore',
] as const;

export type GenericRole = (typeof GENERIC_ROLES)[number];

export const GENERIC_TEMPLATES: Readonly<Record<GenericRole, string>> = {
  delete: 'template:default/project-delete',
  wake: 'template:default/env-wake',
  sleep: 'template:default/env-sleep',
  promote: 'template:default/env-promote',
  configure: 'template:default/env-configure',
  restore: 'template:default/env-restore',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The templates `appType` designates, by role, lower-cased. */
export function declaredTemplates(appType: {
  spec: unknown;
}): Partial<Record<TemplateRole, string>> {
  if (!isRecord(appType.spec)) return {};
  const templates = appType.spec.templates;
  if (!isRecord(templates)) return {};
  const env = isRecord(templates.env) ? templates.env : {};
  const found: Partial<Record<TemplateRole, string>> = {};
  for (const role of TEMPLATE_ROLES) {
    const value =
      role === 'create' || role === 'delete' ? templates[role] : env[role];
    if (typeof value === 'string' && value !== '') {
      found[role] = value.toLowerCase();
    }
  }
  return found;
}

/** The templates the embedded Odoo descriptor designates (header). */
export const EMBEDDED_ODOO_TEMPLATES: ProjectTemplates = declaredTemplates(
  EMBEDDED_ODOO_APP_TYPE,
);

/**
 * The templates of a project of type `appType` whose descriptor is
 * `descriptor` (undefined while unread, missing or failed): what the
 * descriptor designates and nothing else; without it, the embedded Odoo
 * descriptor's for an Odoo project only (header).
 */
export function projectTemplates(
  appType: string | undefined,
  descriptor: Pick<AppTypeDetail, 'spec'> | undefined,
): ProjectTemplates {
  if (descriptor) return declaredTemplates(descriptor);
  return appType === LEGACY_APP_TYPE ? EMBEDDED_ODOO_TEMPLATES : {};
}

/**
 * The roles known by name, for DISPLAY only: the embedded Odoo descriptor's
 * and the generic names.
 */
const STATIC_ROLES = new Map<string, TemplateRole>([
  ...Object.entries(EMBEDDED_ODOO_TEMPLATES).map(
    ([role, ref]) => [ref, role as TemplateRole] as const,
  ),
  ...GENERIC_ROLES.map(role => [GENERIC_TEMPLATES[role], role] as const),
]);

/**
 * The role of a task's template, for DISPLAY: a ref known by name
 * (`STATIC_ROLES`), else the role the given
 * descriptors designate it for — undefined when they designate it for two
 * roles, as the backend ignores such a ref. Undefined for any other ref.
 */
export function roleOfTemplate(
  ref: string | undefined,
  appTypes: ReadonlyArray<Pick<AppTypeDetail, 'spec'>> = [],
): TemplateRole | undefined {
  if (ref === undefined) return undefined;
  const wanted = ref.toLowerCase();
  const known = STATIC_ROLES.get(wanted);
  if (known) return known;
  const roles = new Set<TemplateRole>();
  for (const appType of appTypes) {
    for (const [role, declared] of Object.entries(declaredTemplates(appType))) {
      if (declared === wanted) roles.add(role as TemplateRole);
    }
  }
  const [role] = roles;
  return roles.size === 1 ? role : undefined;
}

/**
 * Le descripteur Odoo embarqué quand il désigne `ref` EN PROPRE — sa
 * création, `ODOO_CREATE_TEMPLATE`, et non un modèle générique que d'autres
 * types désignent aussi — et qu'aucun des descripteurs lus `appTypes` ne
 * s'appelle `odoo` : le repli des verrous du backend
 * (`templateRoleSources`), pour l'AFFICHAGE seulement (`appTitle.ts`,
 * `taskTemplate.ts`). Une tâche de création Odoo est ainsi nommée avant la
 * lecture des descripteurs, et quand ils ne se lisent pas.
 */
export function embeddedOwnerOf(
  ref: string | undefined,
  appTypes: ReadonlyArray<Pick<AppTypeDetail, 'name'>>,
): typeof EMBEDDED_ODOO_APP_TYPE | undefined {
  if (ref === undefined) return undefined;
  if (appTypes.some(appType => appType.name === EMBEDDED_ODOO_APP_TYPE.name)) {
    return undefined;
  }
  const wanted = ref.toLowerCase();
  const generic: readonly string[] = Object.values(GENERIC_TEMPLATES);
  return !generic.includes(wanted) &&
    Object.values(EMBEDDED_ODOO_TEMPLATES).includes(wanted)
    ? EMBEDDED_ODOO_APP_TYPE
    : undefined;
}

/**
 * The scaffolder's `selectedTemplate` route parameters of a template ref
 * `template:<namespace>/<name>`.
 */
export function templateRouteParams(ref: string): {
  namespace: string;
  templateName: string;
} {
  const { namespace, name } = parseEntityRef(ref);
  return { namespace, templateName: name };
}

interface Apis {
  fetchApi: FetchApi;
  discoveryApi: DiscoveryApi;
}

/** As the backend route serves them (`APP_TYPES_TTL_MS`). */
const DESCRIPTOR_TTL_MS = 30_000;

/**
 * One request per type every 30 s for the whole app: every row of the home
 * page and every card of a project page asks for its project's type. What
 * is kept is the promise, so concurrent askers share it; a rejected one is
 * dropped. Off the app's `FetchApi` instance, as `taskProject.ts` does with
 * `ScaffolderApi`: one cache for the app, a fresh one per test.
 */
type DescriptorEntry = {
  at: number;
  promise: Promise<AppTypeDetail | undefined>;
};

const descriptors = new WeakMap<FetchApi, Map<string, DescriptorEntry>>();

export function loadAppType(
  apis: Apis,
  name: string,
): Promise<AppTypeDetail | undefined> {
  const byName =
    descriptors.get(apis.fetchApi) ?? new Map<string, DescriptorEntry>();
  descriptors.set(apis.fetchApi, byName);
  const now = Date.now();
  const cached = byName.get(name);
  if (cached && now - cached.at < DESCRIPTOR_TTL_MS) return cached.promise;
  const promise = fetchAppType({ ...apis, name });
  const entry: DescriptorEntry = { at: now, promise };
  byName.set(name, entry);
  // The caller handles the rejection; this branch only forgets it.
  void promise.catch(() => {
    if (byName.get(name) === entry) byName.delete(name);
  });
  return promise;
}

/**
 * Le cycle de vie d'un projet tel que son `AppType` le déclare (point 8,
 * étape 7) : ses modèles par rôle, son profil (`spec.profile`) et ses
 * fonctions (`spec.features`). L'interface en décide par ces champs, jamais
 * par le nom du type : un bouton d'action n'est offert que si son modèle est
 * déclaré, la CI (colonne, liens, statut) seulement pour un profil
 * `developed`, l'onglet « Données » seulement avec `backups` ou `uploads`.
 *
 * `profile` et `features` sont indéfinis tant que rien ne les dit — le
 * descripteur d'un autre type qu'Odoo non lu, manquant ou en échec —, et les
 * appelants cachent alors ce qui en dépend. `status` dit pourquoi : `loading`
 * tant que le descripteur n'a pas répondu (ou le type pas encore connu),
 * `failed` quand il manque ou échoue, `ready` quand il est lu — et toujours
 * pour Odoo, dont le descripteur embarqué tient lieu.
 */
export interface ProjectLifecycle {
  templates: ProjectTemplates;
  profile: AppProfile | undefined;
  /**
   * The features the TYPE declares, by id — a feature declared with a `when`
   * (`{ id, when, reason }`) included: it may still be off for one
   * environment (`conditionalFeatures`).
   */
  features: ReadonlySet<string> | undefined;
  /**
   * The features declared with a `when`: on or off per environment, over its
   * values (`featureEnabled` of the engine; the upload card,
   * `UploadCardForEnvironment`). Undefined as `features`.
   */
  conditionalFeatures: ReadonlySet<string> | undefined;
  status: 'loading' | 'failed' | 'ready';
}

/** `spec.profile` of `appType`, when it is one the schema allows. */
function declaredProfile(appType: { spec: unknown }): AppProfile | undefined {
  if (!isRecord(appType.spec)) return undefined;
  const profile = appType.spec.profile;
  return profile === 'developed' || profile === 'standard'
    ? profile
    : undefined;
}

/**
 * `spec.features` of `appType` — its strings and the ids of its `{ id,
 * when }` entries — and the latter apart; undefined without the list.
 */
function declaredFeatures(appType: {
  spec: unknown;
}): { all: ReadonlySet<string>; conditional: ReadonlySet<string> } | undefined {
  if (!isRecord(appType.spec) || !Array.isArray(appType.spec.features)) {
    return undefined;
  }
  const all = new Set<string>();
  const conditional = new Set<string>();
  for (const feature of appType.spec.features) {
    if (typeof feature === 'string') {
      all.add(feature);
    } else if (isRecord(feature) && typeof feature.id === 'string') {
      all.add(feature.id);
      conditional.add(feature.id);
    }
  }
  return { all, conditional };
}

/** The lifecycle `appType` declares (`ProjectLifecycle`). */
export function declaredLifecycle(appType: {
  spec: unknown;
}): ProjectLifecycle {
  const features = declaredFeatures(appType);
  return {
    templates: declaredTemplates(appType),
    profile: declaredProfile(appType),
    features: features?.all,
    conditionalFeatures: features?.conditional,
    status: 'ready',
  };
}

/** What the embedded Odoo descriptor declares (header of `templateRoles`). */
export const EMBEDDED_ODOO_LIFECYCLE: ProjectLifecycle = {
  ...declaredLifecycle(EMBEDDED_ODOO_APP_TYPE),
  templates: EMBEDDED_ODOO_TEMPLATES,
};

const LOADING_LIFECYCLE: ProjectLifecycle = {
  templates: {},
  profile: undefined,
  features: undefined,
  conditionalFeatures: undefined,
  status: 'loading',
};

const FAILED_LIFECYCLE: ProjectLifecycle = {
  ...LOADING_LIFECYCLE,
  status: 'failed',
};

/**
 * The lifecycle of a project of type `appType` whose descriptor is
 * `descriptor` (undefined while unread, missing or failed; `read`: it
 * answered), by the rule of `projectTemplates`: what the descriptor
 * declares; without it, the embedded Odoo descriptor's for an Odoo project
 * only, nothing for another — `loading` until it answered, `failed` after.
 */
export function projectLifecycle(
  appType: string | undefined,
  descriptor: Pick<AppTypeDetail, 'spec'> | undefined,
  read = false,
): ProjectLifecycle {
  if (descriptor) return declaredLifecycle(descriptor);
  if (appType === LEGACY_APP_TYPE) return EMBEDDED_ODOO_LIFECYCLE;
  return read ? FAILED_LIFECYCLE : LOADING_LIFECYCLE;
}

/** Whether `lifecycle` declares `feature`; false while it is unknown. */
export function hasFeature(
  lifecycle: Pick<ProjectLifecycle, 'features'>,
  feature: string,
): boolean {
  return lifecycle.features?.has(feature) ?? false;
}

/**
 * Whether the project has a CI of its own to show — profile `developed`
 * only: false for `standard`, and while the profile is unknown.
 */
export function showsCi(lifecycle: Pick<ProjectLifecycle, 'profile'>): boolean {
  return lifecycle.profile === 'developed';
}

/**
 * The lifecycle of a project of `appType` (`appTypeOf`), by
 * `projectLifecycle`, from the shared `loadAppType` cache: for an Odoo
 * project the embedded descriptor's until its descriptor is read, and for
 * good when it cannot be (missing, failed); for another type, or an
 * `appType` still unknown (undefined), only what its descriptor declares
 * once read — no template, profile nor features meanwhile, the actions and
 * the CI not offered by the callers.
 */
export function useProjectLifecycle(
  appType: string | undefined,
): ProjectLifecycle {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [loaded, setLoaded] = useState<{
    name: string;
    appType: AppTypeDetail | undefined;
  }>();

  useEffect(() => {
    let active = true;
    if (appType !== undefined) {
      loadAppType({ fetchApi, discoveryApi }, appType).then(
        found => {
          if (active) setLoaded({ name: appType, appType: found });
        },
        () => {
          if (active) setLoaded({ name: appType, appType: undefined });
        },
      );
    }
    return () => {
      active = false;
    };
  }, [fetchApi, discoveryApi, appType]);

  const read = loaded !== undefined && loaded.name === appType;
  const descriptor = read ? loaded.appType : undefined;
  return useMemo(
    () => projectLifecycle(appType, descriptor, read),
    [appType, descriptor, read],
  );
}

/**
 * Of the types `appTypes`, those whose project shows a CI (`showsCi`), each
 * descriptor read once through the `loadAppType` cache: Odoo at once (its
 * embedded descriptor until its own answers), another type only once its
 * descriptor says `developed` — never while it loads, nor when it fails. A
 * new set only when its members change.
 */
export function useCiAppTypes(
  appTypes: readonly string[],
): ReadonlySet<string> {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [loaded, setLoaded] = useState<
    ReadonlyMap<string, AppTypeDetail | undefined>
  >(new Map());
  const wanted = [...new Set(appTypes)].sort().join('\n');

  useEffect(() => {
    let active = true;
    const names = wanted === '' ? [] : wanted.split('\n');
    names.forEach(name => {
      const store = (found: AppTypeDetail | undefined) => {
        if (active) {
          setLoaded(current => new Map(current).set(name, found));
        }
      };
      loadAppType({ fetchApi, discoveryApi }, name).then(store, () =>
        store(undefined),
      );
    });
    return () => {
      active = false;
    };
  }, [fetchApi, discoveryApi, wanted]);

  const ci =
    wanted === ''
      ? ''
      : wanted
          .split('\n')
          .filter(name =>
            showsCi(projectLifecycle(name, loaded.get(name), loaded.has(name))),
          )
          .join('\n');
  return useMemo(() => new Set(ci === '' ? [] : ci.split('\n')), [ci]);
}

/**
 * The templates of a project of `appType` (`useProjectLifecycle`): for an
 * Odoo project the embedded descriptor's until its descriptor is read, and
 * for good when it cannot be (missing, failed); for another type, or an
 * `appType` still unknown (undefined), only what its descriptor designates
 * once read — a role absent meanwhile, its launch not offered by the caller.
 */
export function useProjectTemplates(
  appType: string | undefined,
): ProjectTemplates {
  return useProjectLifecycle(appType).templates;
}

/**
 * The template of `role` for a project of `appType` (`useProjectTemplates`);
 * undefined while it has none.
 */
export function useTemplateFor(
  appType: string | undefined,
  role: TemplateRole,
): string | undefined {
  return useProjectTemplates(appType)[role];
}

export type AppTypeDescriptorsState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ready'; items: AppTypeDetail[] };

/**
 * How long `useAppTypeDescriptors` waits for the descriptors before it
 * answers `failed`: the callers then do what they do when the route fails
 * (every template, the names known statically), so that no page waits on a request
 * that does not end. A later answer is ignored, so that nothing shown
 * changes under the reader's eyes.
 */
export const DESCRIPTORS_WAIT_MS = 10_000;

const IDLE: AppTypeDescriptorsState = { status: 'idle' };

/**
 * Every application type with its descriptor — the list, then each type —,
 * read once per mount; `idle` without reading while `enabled` is false.
 * `failed` when the list or any type fails, or after `DESCRIPTORS_WAIT_MS`:
 * a partial set of descriptors would hide a template one of the others
 * designates.
 */
export function useAppTypeDescriptors(
  options: { enabled?: boolean } = {},
): AppTypeDescriptorsState {
  const enabled = options.enabled ?? true;
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [state, setState] = useState<AppTypeDescriptorsState>({
    status: 'loading',
  });

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    setState({ status: 'loading' });
    const timer = setTimeout(() => {
      if (active) setState({ status: 'failed' });
      active = false;
    }, DESCRIPTORS_WAIT_MS);
    const load = async () => {
      try {
        const apis = { fetchApi, discoveryApi };
        const summaries = await fetchAppTypes(apis);
        const details = await Promise.all(
          summaries.map(summary => loadAppType(apis, summary.name)),
        );
        const items = details.filter(
          (detail): detail is AppTypeDetail => detail !== undefined,
        );
        if (active) setState({ status: 'ready', items });
      } catch {
        if (active) setState({ status: 'failed' });
      } finally {
        clearTimeout(timer);
      }
    };
    // `load` turns its own failure into state, so nothing can reject.
    void load();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [fetchApi, discoveryApi, enabled]);

  return enabled ? state : IDLE;
}
